// =============================================================================
// VandieZ admin-web -- Vehicles search (/vehicles)
// =============================================================================
// Companion to /vehicles/queue (which is the pending-only review surface).
// This page is a search across ALL vehicles regardless of status, with a
// registration-number search box. Operators reach it when they need to
// find a specific vehicle they're not currently reviewing.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  Truck,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { vehicleAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { VehicleAdminQueueEntry } from "@/types/vehicle";

const PAGE = 50;

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Deactivated", value: "deactivated" },
];

function statusClass(s: string): string {
  switch (s) {
    case "pending":
      return "bg-warning-subtle text-warning-subtle-foreground";
    case "approved":
      return "bg-success-subtle text-success-subtle-foreground";
    case "rejected":
    case "deactivated":
      return "bg-danger-subtle text-danger-subtle-foreground";
    default:
      return "bg-muted text-foreground";
  }
}

export function VehiclesSearchPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = searchParams.get("status") ?? "approved";
  const qFilter = searchParams.get("q") ?? "";
  const offset = Number(searchParams.get("offset") ?? "0");
  // Curation worklist filter (Task 5): "false" = model not set, omitted =
  // no filtering. There's no "true" toggle on this page today -- the only
  // operator need so far is finding vehicles that still need one.
  const modelSetFilter = searchParams.get("model_set");

  const [rows, setRows] = useState<VehicleAdminQueueEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qInput, setQInput] = useState(qFilter);

  const fetchRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await vehicleAdminApi.queue(
          accessToken,
          {
            status: statusFilter,
            ...(qFilter !== "" ? { q: qFilter } : {}),
            ...(modelSetFilter !== null
              ? { model_set: modelSetFilter === "true" }
              : {}),
            limit: PAGE,
            offset,
          },
          signal,
        );
        setRows(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load vehicles.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, statusFilter, qFilter, modelSetFilter, offset],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRows(c.signal);
    return () => { c.abort(); };
  }, [fetchRows]);

  const updateParam = (key: string, value: string): void => {
    setSearchParams((cur) => {
      const next = new URLSearchParams(cur);
      if (value === "") {next.delete(key);} else {next.set(key, value);}
      next.delete("offset");
      return next;
    }, { replace: true });
  };

  const page = Math.floor(offset / PAGE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vehicles"
        description={`Search across every vehicle on the platform — not just the pending queue. ${total} matching filters.`}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" /> Filters
          </CardTitle>
          <CardDescription>
            Status is required (defaults to "Approved"); reg-number is
            optional ILIKE.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <div className="mt-1 flex flex-wrap gap-1">
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { updateParam("status", o.value); }}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs",
                    statusFilter === o.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent",
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Registration number contains
            </label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateParam("q", qInput.trim().toUpperCase());
              }}
            >
              <Input
                value={qInput}
                onChange={(e) => { setQInput(e.target.value); }}
                placeholder="e.g. TN05"
                className="mt-1 font-mono uppercase"
              />
            </form>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Catalog curation
            </label>
            <div className="mt-1 flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => {
                  updateParam(
                    "model_set",
                    modelSetFilter === "false" ? "" : "false",
                  );
                }}
                className={cn(
                  "rounded-full px-3 py-1 text-xs",
                  modelSetFilter === "false"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent",
                )}
              >
                Model not set
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" /> Results
          </CardTitle>
          <CardDescription>
            {rows.length} on this page · page {page} / {totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loading && rows.length === 0 ? (
            <TableSkeleton rows={6} columns={4} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No matching vehicles.
            </div>
          ) : (
            <ul className="divide-y text-sm">
              {rows.map((v) => (
                <li key={v.id}>
                  <Link
                    to={`/vehicles/${v.id}`}
                    className="flex items-center justify-between gap-3 px-2 py-3 hover:bg-muted/40 transition-colors -mx-2 rounded"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-medium">
                          {v.registration_number}
                        </span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs uppercase",
                            statusClass(statusFilter),
                          )}
                        >
                          {statusFilter}
                        </span>
                        {v.vahan_verified && (
                          <span className="rounded-full bg-info-subtle text-info-subtle-foreground px-2 py-0.5 text-xs">
                            vahan
                          </span>
                        )}
                        {v.vehicle_model_display_name !== null ? (
                          <span className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-xs">
                            {v.vehicle_model_display_name}
                          </span>
                        ) : (
                          v.vehicle_model_id === null && (
                            <span className="rounded-full bg-warning-subtle text-warning-subtle-foreground px-2 py-0.5 text-xs">
                              no catalog model
                            </span>
                          )
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {v.make} {v.model} · owner{" "}
                        {v.owner_name ?? v.owner_id.slice(0, 8)} ·{" "}
                        {new Date(v.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => {
                setSearchParams((cur) => {
                  const next = new URLSearchParams(cur);
                  const newOffset = Math.max(0, offset - PAGE);
                  if (newOffset === 0) {next.delete("offset");}
                  else {next.set("offset", String(newOffset));}
                  return next;
                });
              }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE >= total}
              onClick={() => {
                setSearchParams((cur) => {
                  const next = new URLSearchParams(cur);
                  next.set("offset", String(offset + PAGE));
                  return next;
                });
              }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
